/* =====================================================================
   Wise — nouvelle page d'accueil : socle JS « WH » (agent Fondations)
   Chargé en defer sur l'accueil, avant wh-hero.js, wh-shop.js, wh-real.js
   et wh-loyal.js. Sans dépendance, ES5. Expose window.WH (voir CORE.md) :
     WH.ready(fn)       fn(scope) au chargement (scope = document), puis à chaque
                        section rechargée dans l'éditeur (scope = la section)
     WH.unload(fn)      fn(section) juste avant qu'une section soit retirée (éditeur)
     WH.init(scope)     initialise défileurs, onglets et apparitions dans scope
     WH.reduced         mouvement réduit (booléen tenu à jour) ; WH.onReduced(fn)
     WH.design          true dans l'éditeur de thème
     WH.reveal(scope)   apparitions [data-wh-reveal]
     WH.scroller(el)    défileur [data-wh-scroller]
     WH.tabs(el)        onglets [data-wh-tabs]
     WH.inView(el, onEnter, onLeave, options)
     WH.money(cents)    prix au format de la boutique (« 12,99 € »)
     WH.debounce, WH.esc, WH.all, WH.storage
   ===================================================================== */
(function (window, document) {
  'use strict';

  if (window.WH && window.WH.version) return;

  var WH = {};
  var root = document.documentElement;
  var hasIO = 'IntersectionObserver' in window;
  var hasRO = 'ResizeObserver' in window;
  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var booted = false;
  var started = false;
  var readyFns = [];
  var unloadFns = [];
  var reducedFns = [];
  var registry = [];
  var revealIO = null;
  var decoder = null;
  var uid = 0;

  WH.version = '1.0.0';
  WH.reduced = !!(mqReduce && mqReduce.matches);
  WH.design = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- Utilitaires ---------- */
  function noop() {}

  function report(err) {
    if (window.console && typeof window.console.error === 'function') window.console.error('[WH]', err);
  }

  /* Appelle fn sans jamais laisser une erreur casser le reste de la page. */
  function safe(fn, a, b) {
    try {
      return fn(a, b);
    } catch (err) {
      report(err);
      return undefined;
    }
  }

  function all(selector, ctx) {
    var base = ctx || document;
    if (!base || typeof base.querySelectorAll !== 'function') return [];
    return Array.prototype.slice.call(base.querySelectorAll(selector));
  }

  function matches(el, selector) {
    if (!el || el.nodeType !== 1) return false;
    var fn = el.matches || el.webkitMatchesSelector || el.msMatchesSelector;
    return !!fn && fn.call(el, selector);
  }

  function closest(node, selector) {
    var el = node && node.nodeType === 1 ? node : node && node.parentNode;
    while (el && el.nodeType === 1) {
      if (matches(el, selector)) return el;
      el = el.parentNode;
    }
    return null;
  }

  /* Éléments de scope correspondant au sélecteur, scope compris. */
  function within(scope, selector) {
    var base = scope || document;
    var list = all(selector, base);
    if (matches(base, selector)) list.unshift(base);
    return list;
  }

  function emit(el, name, detail) {
    var ev;
    try {
      ev = new window.CustomEvent(name, { bubbles: true, detail: detail });
    } catch (err) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail);
    }
    el.dispatchEvent(ev);
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function debounce(fn, wait) {
    var timer = 0;
    var delay = typeof wait === 'number' ? wait : 150;
    return function () {
      var ctx = this;
      var args = arguments;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        fn.apply(ctx, args);
      }, delay);
    };
  }

  function behavior() {
    return WH.reduced ? 'auto' : 'smooth';
  }

  function scrollToX(el, left) {
    if (typeof el.scrollTo === 'function') {
      try {
        el.scrollTo({ left: left, behavior: behavior() });
        return;
      } catch (err) {
        /* navigateur sans options de défilement : repli ci-dessous */
      }
    }
    el.scrollLeft = left;
  }

  /* Durée maximale (ms) d'une liste CSS du type « 0.6s, 120ms ». */
  function maxTime(list) {
    var parts = String(list || '').split(',');
    var max = 0;
    for (var i = 0; i < parts.length; i++) {
      var raw = parts[i].replace(/^\s+|\s+$/g, '');
      var value = parseFloat(raw) || 0;
      if (raw.indexOf('ms') === -1) value *= 1000;
      if (value > max) max = value;
    }
    return max;
  }

  WH.all = all;
  WH.esc = esc;
  WH.debounce = debounce;

  /* ---------- Mouvement réduit ---------- */
  function onReducedChange(e) {
    WH.reduced = !!e.matches;
    var list = reducedFns.slice();
    for (var i = 0; i < list.length; i++) safe(list[i], WH.reduced);
  }

  if (mqReduce) {
    if (typeof mqReduce.addEventListener === 'function') mqReduce.addEventListener('change', onReducedChange);
    else if (typeof mqReduce.addListener === 'function') mqReduce.addListener(onReducedChange);
  }

  WH.onReduced = function (fn) {
    if (typeof fn !== 'function') return noop;
    reducedFns.push(fn);
    return function () {
      var i = reducedFns.indexOf(fn);
      if (i > -1) reducedFns.splice(i, 1);
    };
  };

  /* ---------- Prix ---------- */
  function decodeEntities(text) {
    if (text.indexOf('&') === -1) return text;
    decoder = decoder || document.createElement('textarea');
    decoder.innerHTML = text;
    return decoder.value;
  }

  function formatAmount(cents, decimals, thousands, decimal) {
    var value = Number(cents);
    if (!isFinite(value)) value = 0;
    var parts = (Math.round(value) / 100).toFixed(decimals).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return decimals > 0 ? whole + decimal + parts[1] : whole;
  }

  /* Centimes -> texte, avec le format de la boutique (window.WiseSettings.moneyFormat).
     Renvoie du texte brut : l'insérer avec textContent (ou WH.esc avant innerHTML). */
  WH.money = function (cents, format) {
    var settings = window.WiseSettings || {};
    var fmt = String(format || settings.moneyFormat || '{{amount_with_comma_separator}} €');
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = match ? match[1] : 'amount_with_comma_separator';
    var amount;
    switch (key) {
      case 'amount_no_decimals':
        amount = formatAmount(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = formatAmount(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = formatAmount(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = formatAmount(cents, 2, '\'', '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = formatAmount(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        amount = formatAmount(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        amount = formatAmount(cents, 2, ' ', '.');
        break;
      default:
        amount = formatAmount(cents, 2, ',', '.');
    }
    var text = match ? fmt.replace(match[0], function () { return amount; }) : amount + ' €';
    return decodeEntities(text.replace(/<[^>]*>/g, '')).replace(/^\s+|\s+$/g, '');
  };

  /* ---------- Stockage local (lecture et écriture JSON sans risque) ---------- */
  WH.storage = {
    get: function (key, fallback) {
      try {
        var raw = window.localStorage.getItem(key);
        return raw == null ? fallback : JSON.parse(raw);
      } catch (err) {
        return fallback;
      }
    },
    set: function (key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (err) {
        return false;
      }
    }
  };

  /* ---------- Visibilité à l'écran ---------- */
  /* onEnter(el, entry) quand au moins options.threshold (défaut 0.25) de l'élément est visible,
     onLeave(el, entry) quand il repasse sous ce seuil. Options : threshold, rootMargin, once.
     Renvoie une fonction qui arrête l'observation. */
  WH.inView = function (el, onEnter, onLeave, options) {
    if (!el || el.nodeType !== 1) return noop;
    var opts = options || {};
    var threshold = typeof opts.threshold === 'number' ? Math.max(0, Math.min(1, opts.threshold)) : 0.25;
    var inside = false;
    var stopped = false;
    var io = null;

    function stop() {
      if (stopped) return;
      stopped = true;
      if (io) io.disconnect();
    }

    if (!hasIO) {
      if (typeof onEnter === 'function') safe(onEnter, el, null);
      return noop;
    }

    io = new window.IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        var visible = threshold > 0
          ? entry.isIntersecting && entry.intersectionRatio >= threshold - 0.001
          : entry.isIntersecting;
        if (visible && !inside) {
          inside = true;
          if (typeof onEnter === 'function') safe(onEnter, el, entry);
          if (opts.once) {
            stop();
            return;
          }
        } else if (!visible && inside) {
          inside = false;
          if (typeof onLeave === 'function') safe(onLeave, el, entry);
        }
      }
    }, {
      rootMargin: opts.rootMargin || '0px',
      threshold: threshold > 0 ? [0, threshold] : 0
    });
    io.observe(el);
    return stop;
  };

  /* ---------- Instances (défileurs, onglets) ---------- */
  function register(el, instance) {
    registry.push({ el: el, instance: instance });
    return instance;
  }

  function destroyWithin(scope) {
    var keep = [];
    for (var i = 0; i < registry.length; i++) {
      var rec = registry[i];
      if (scope === rec.el || scope.contains(rec.el)) safe(rec.instance.destroy);
      else keep.push(rec);
    }
    registry = keep;
  }

  /* ---------- Apparition au défilement [data-wh-reveal] ---------- */
  function revealNow(el, animate) {
    if (!el.classList.contains('is-in')) {
      if (animate && !WH.reduced && !WH.design) {
        el.classList.add('is-anim');
        el.classList.add('is-in');
        var cs = window.getComputedStyle(el);
        var total = maxTime(cs.transitionDelay) + maxTime(cs.transitionDuration);
        /* fin de l'animation : on rend la main aux transitions propres de la section */
        window.setTimeout(function () {
          el.classList.remove('is-anim');
        }, total + 80);
      } else {
        el.classList.add('is-in');
      }
    }
    settleFx(el);
  }

  function revealAll(scope) {
    var list = within(scope || document, '[data-wh-reveal]');
    for (var i = 0; i < list.length; i++) list[i].classList.add('is-in');
  }

  /* Rang d'un élément dans son groupe [data-wh-stagger] pour ce lot d'apparitions. */
  function rankIn(groups, group) {
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].el === group) return groups[i].count++;
    }
    groups.push({ el: group, count: 1 });
    return 0;
  }

  function onRevealEntries(entries) {
    var groups = [];
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry.isIntersecting) continue;
      var el = entry.target;
      revealIO.unobserve(el);
      var group = closest(el.parentNode, '[data-wh-stagger]');
      if (group) {
        var step = parseInt(group.getAttribute('data-wh-stagger'), 10);
        if (!(step >= 0)) step = 80;
        el.style.setProperty('--wh-d', Math.min(rankIn(groups, group), 6) * step + 'ms');
      }
      revealNow(el, true);
    }
  }

  WH.reveal = function (scope) {
    var list = within(scope || document, '[data-wh-reveal]');
    if (!list.length) return;
    var viewport = window.innerHeight || root.clientHeight;
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (el.__whRevealSeen) continue;
      el.__whRevealSeen = true;
      if (el.classList.contains('is-in')) continue;
      if (WH.design || WH.reduced || !hasIO) {
        revealNow(el, false);
        continue;
      }
      var rect = el.getBoundingClientRect();
      if ((rect.width || rect.height) && rect.bottom > 0 && rect.top < viewport) {
        /* déjà à l'écran au chargement : affiché tel quel, sans animation */
        revealNow(el, false);
      } else {
        if (!revealIO) {
          revealIO = new window.IntersectionObserver(onRevealEntries, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
        }
        revealIO.observe(el);
      }
    }
  };

  function forgetReveal(scope) {
    if (!revealIO) return;
    var list = within(scope, '[data-wh-reveal]');
    for (var i = 0; i < list.length; i++) revealIO.unobserve(list[i]);
  }

  /* wise-v23-fx.js anime seul les .card qui ne sont pas dans un [data-reveal] : dans un
     [data-wh-reveal], c'est le conteneur qui apparaît et les cartes le suivent. */
  function settleFx(scope) {
    var list = all('[data-wh-reveal] .v23-reveal:not(.is-in)', scope);
    if (matches(scope, '[data-wh-reveal]')) list = list.concat(all('.v23-reveal:not(.is-in)', scope));
    for (var i = 0; i < list.length; i++) list[i].classList.add('is-in');
  }

  /* ---------- Défileur horizontal [data-wh-scroller] ---------- */
  function createScroller(el) {
    var track = matches(el, '[data-wh-track], .wh-scroller__track')
      ? el
      : el.querySelector('[data-wh-track]') || el.querySelector('.wh-scroller__track');
    if (!track) return null;

    var prev = el.querySelector('[data-wh-prev]');
    var next = el.querySelector('[data-wh-next]');
    var bar = el.querySelector('[data-wh-bar]');
    var thumb = null;
    var frame = 0;
    var ro = null;
    var lastSize = -1;

    if (bar) {
      thumb = bar.querySelector('[data-wh-thumb]') || bar.firstElementChild;
      if (!thumb) {
        thumb = document.createElement('span');
        bar.appendChild(thumb);
      }
    }
    if (!track.id) track.id = 'wh-track-' + (++uid);
    if (prev) prev.setAttribute('aria-controls', track.id);
    if (next) next.setAttribute('aria-controls', track.id);

    function items() {
      var out = [];
      var kids = track.children;
      for (var i = 0; i < kids.length; i++) {
        if (kids[i].offsetWidth > 0) out.push(kids[i]);
      }
      return out;
    }

    function padStart() {
      return parseFloat(window.getComputedStyle(track).scrollPaddingLeft) || 0;
    }

    /* Position de départ d'un élément dans la zone défilante du rail. */
    function posOf(item) {
      return item.getBoundingClientRect().left - track.getBoundingClientRect().left - track.clientLeft + track.scrollLeft;
    }

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    function perPage(list) {
      var first = list[0].getBoundingClientRect();
      var step = list.length > 1 ? list[1].getBoundingClientRect().left - first.left : first.width;
      if (!(step > 0)) return 1;
      var gap = Math.max(0, step - first.width);
      var cs = window.getComputedStyle(track);
      var pads = (parseFloat(cs.scrollPaddingLeft) || 0) + (parseFloat(cs.scrollPaddingRight) || 0);
      return Math.max(1, Math.floor((track.clientWidth - pads + gap + 1) / step));
    }

    /* Avance ou recule d'une page (byPage) ou d'un élément, en restant aligné sur les éléments. */
    function move(dir, byPage) {
      var list = items();
      if (!list.length) return;
      var pad = padStart();
      var x = track.scrollLeft;
      var count = byPage ? perPage(list) : 1;
      var start = list.length - 1;
      for (var i = 0; i < list.length; i++) {
        if (posOf(list[i]) - pad >= x - 4) {
          start = i;
          break;
        }
      }
      var target = dir > 0 ? Math.min(list.length - 1, start + count) : Math.max(0, start - count);
      scrollToX(track, Math.max(0, Math.min(maxScroll(), posOf(list[target]) - pad)));
    }

    function setDisabled(btn, off) {
      if (btn) btn.setAttribute('aria-disabled', off ? 'true' : 'false');
    }

    function update() {
      frame = 0;
      var max = maxScroll();
      var x = Math.abs(track.scrollLeft);
      var isStatic = max <= 2;
      el.classList.toggle('is-static', isStatic);
      setDisabled(prev, isStatic || x <= 2);
      setDisabled(next, isStatic || x >= max - 2);
      if (thumb) {
        var size = isStatic || !track.scrollWidth ? 1 : Math.min(1, Math.max(0.08, track.clientWidth / track.scrollWidth));
        var pos = max > 0 ? Math.min(1, Math.max(0, x / max)) : 0;
        if (size !== lastSize) {
          lastSize = size;
          thumb.style.width = (size * 100).toFixed(2) + '%';
        }
        thumb.style.transform = 'translateX(' + (pos * (1 / size - 1) * 100).toFixed(2) + '%)';
      }
    }

    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(update);
    }

    function onPrev() {
      if (prev.getAttribute('aria-disabled') !== 'true') move(-1, true);
    }

    function onNext() {
      if (next.getAttribute('aria-disabled') !== 'true') move(1, true);
    }

    function onKey(e) {
      if (e.target !== track) return;
      var key = e.key;
      if (key === 'ArrowRight' || key === 'Right') {
        e.preventDefault();
        move(1, false);
      } else if (key === 'ArrowLeft' || key === 'Left') {
        e.preventDefault();
        move(-1, false);
      } else if (key === 'Home') {
        e.preventDefault();
        scrollToX(track, 0);
      } else if (key === 'End') {
        e.preventDefault();
        scrollToX(track, maxScroll());
      }
    }

    /* Amène dans la zone visible l'élément qui contient target (bloc sélectionné dans l'éditeur). */
    function show(target) {
      var list = items();
      for (var i = 0; i < list.length; i++) {
        if (list[i] === target || list[i].contains(target)) {
          scrollToX(track, Math.max(0, Math.min(maxScroll(), posOf(list[i]) - padStart())));
          return true;
        }
      }
      return false;
    }

    function destroy() {
      track.removeEventListener('scroll', schedule);
      track.removeEventListener('keydown', onKey);
      if (prev) prev.removeEventListener('click', onPrev);
      if (next) next.removeEventListener('click', onNext);
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', schedule);
      window.removeEventListener('load', schedule);
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      el.__whScroller = null;
    }

    track.addEventListener('scroll', schedule, { passive: true });
    track.addEventListener('keydown', onKey);
    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);
    if (hasRO) {
      ro = new window.ResizeObserver(schedule);
      ro.observe(track);
    } else {
      window.addEventListener('resize', schedule);
    }
    window.addEventListener('load', schedule);
    update();

    return {
      el: el,
      track: track,
      update: update,
      next: function () { move(1, true); },
      prev: function () { move(-1, true); },
      show: show,
      destroy: destroy
    };
  }

  WH.scroller = function (el) {
    if (!el || el.nodeType !== 1) return null;
    if (el.__whScroller) return el.__whScroller;
    var api = createScroller(el);
    if (!api) return null;
    el.__whScroller = api;
    return register(el, api);
  };

  /* ---------- Onglets [data-wh-tabs] ---------- */
  function createTabs(el) {
    var list = el.querySelector('[role="tablist"]');
    if (!list) return null;
    var tabs = all('[role="tab"]', list);
    if (!tabs.length) return null;
    var current = -1;
    var timer = 0;

    function panelOf(tab) {
      var id = tab.getAttribute('aria-controls');
      return id ? document.getElementById(id) : null;
    }

    /* Onglet actif visible dans une liste défilante (mobile). */
    function keepVisible(tab, instant) {
      if (list.scrollWidth <= list.clientWidth + 1) return;
      var lr = list.getBoundingClientRect();
      var tr = tab.getBoundingClientRect();
      if (tr.left >= lr.left && tr.right <= lr.right) return;
      var left = Math.max(0, list.scrollLeft + (tr.left - lr.left) - (lr.width - tr.width) / 2);
      if (instant) list.scrollLeft = left;
      else scrollToX(list, left);
    }

    function animate(panel) {
      if (WH.reduced || WH.design) return;
      panel.classList.remove('is-entering');
      void panel.offsetWidth; /* relance l'animation CSS */
      panel.classList.add('is-entering');
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        panel.classList.remove('is-entering');
      }, 700);
    }

    function refresh(panel) {
      var scrollers = within(panel, '[data-wh-scroller]');
      for (var i = 0; i < scrollers.length; i++) {
        if (scrollers[i].__whScroller) scrollers[i].__whScroller.update();
      }
    }

    function select(index, opts) {
      var o = opts || {};
      if (index < 0 || index >= tabs.length) return;
      var changed = index !== current;
      for (var i = 0; i < tabs.length; i++) {
        var on = i === index;
        var panel = panelOf(tabs[i]);
        tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
        tabs[i].setAttribute('tabindex', on ? '0' : '-1');
        tabs[i].classList.toggle('is-active', on);
        if (panel) {
          panel.hidden = !on;
          if (!on) panel.classList.remove('is-entering');
        }
      }
      current = index;
      var active = tabs[index];
      var activePanel = panelOf(active);
      if (o.focus) active.focus({ preventScroll: true });
      if (!changed) return;
      if (activePanel && o.animate) animate(activePanel);
      keepVisible(active, !!o.instant);
      if (activePanel) refresh(activePanel);
      if (!o.silent) emit(el, 'wh:tabchange', { index: index, tab: active, panel: activePanel });
    }

    function onClick(e) {
      var tab = closest(e.target, '[role="tab"]');
      var i = tab ? tabs.indexOf(tab) : -1;
      if (i < 0) return;
      if (tab.tagName === 'A') e.preventDefault();
      select(i, { animate: true });
    }

    function onKey(e) {
      var i = tabs.indexOf(e.target);
      if (i < 0) return;
      var last = tabs.length - 1;
      var key = e.key;
      var to = -1;
      if (key === 'ArrowRight' || key === 'Right') to = i === last ? 0 : i + 1;
      else if (key === 'ArrowLeft' || key === 'Left') to = i === 0 ? last : i - 1;
      else if (key === 'Home') to = 0;
      else if (key === 'End') to = last;
      if (to < 0) return;
      e.preventDefault();
      select(to, { focus: true, animate: true });
    }

    /* Bloc sélectionné dans l'éditeur : affiche l'onglet qui le contient. */
    function show(target) {
      var tab = closest(target, '[role="tab"]');
      var index = tab ? tabs.indexOf(tab) : -1;
      for (var i = 0; index < 0 && i < tabs.length; i++) {
        var panel = panelOf(tabs[i]);
        if (panel && (panel === target || panel.contains(target))) index = i;
      }
      if (index > -1) select(index, {});
      return index > -1;
    }

    function destroy() {
      list.removeEventListener('click', onClick);
      list.removeEventListener('keydown', onKey);
      window.clearTimeout(timer);
      el.__whTabs = null;
    }

    var initial = 0;
    for (var k = 0; k < tabs.length; k++) {
      if (tabs[k].getAttribute('aria-selected') === 'true' || tabs[k].classList.contains('is-active')) {
        initial = k;
        break;
      }
    }
    if (!list.hasAttribute('aria-orientation')) list.setAttribute('aria-orientation', 'horizontal');
    list.addEventListener('click', onClick);
    list.addEventListener('keydown', onKey);
    select(initial, { silent: true, instant: true });

    return {
      el: el,
      tabs: tabs,
      index: function () { return current; },
      select: function (index) { select(index, { animate: true }); },
      show: show,
      destroy: destroy
    };
  }

  WH.tabs = function (el) {
    if (!el || el.nodeType !== 1) return null;
    if (el.__whTabs) return el.__whTabs;
    var api = createTabs(el);
    if (!api) return null;
    el.__whTabs = api;
    return register(el, api);
  };

  /* ---------- Initialisation ---------- */
  function init(scope) {
    var base = scope || document;
    var i;
    var tabsList = within(base, '[data-wh-tabs]');
    for (i = 0; i < tabsList.length; i++) safe(WH.tabs, tabsList[i]);
    var scrollers = within(base, '[data-wh-scroller]');
    for (i = 0; i < scrollers.length; i++) safe(WH.scroller, scrollers[i]);
    safe(WH.reveal, base);
    safe(settleFx, base);
  }

  WH.init = init;

  WH.ready = function (fn) {
    if (typeof fn !== 'function') return;
    readyFns.push(fn);
    if (started) safe(fn, document);
  };

  WH.unload = function (fn) {
    if (typeof fn === 'function') unloadFns.push(fn);
  };

  /* Premier passage : ce qui est déjà à l'écran reste visible, puis html.wh-js
     active le masquage du reste (aucun clignotement). */
  function boot() {
    if (booted) return;
    booted = true;
    try {
      WH.reveal(document);
    } catch (err) {
      report(err);
      revealAll(document);
    }
    root.classList.add('wh-js');
  }

  function start() {
    if (started) return;
    started = true;
    boot();
    init(document);
    var list = readyFns.slice();
    for (var i = 0; i < list.length; i++) safe(list[i], document);
  }

  /* ---------- Éditeur de thème ---------- */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target;
    if (!section || section.nodeType !== 1) return;
    init(section);
    var list = readyFns.slice();
    for (var i = 0; i < list.length; i++) safe(list[i], section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target;
    if (!section || section.nodeType !== 1) return;
    var list = unloadFns.slice();
    for (var i = 0; i < list.length; i++) safe(list[i], section);
    destroyWithin(section);
    forgetReveal(section);
  });

  document.addEventListener('shopify:block:select', function (e) {
    var target = e.target;
    var tabsEl = closest(target, '[data-wh-tabs]');
    if (tabsEl && tabsEl.__whTabs) safe(tabsEl.__whTabs.show, target);
    var scrollerEl = closest(target, '[data-wh-scroller]');
    if (scrollerEl && scrollerEl.__whScroller) safe(scrollerEl.__whScroller.show, target);
  });

  window.addEventListener('beforeprint', function () {
    revealAll(document);
  });

  if (WH.design) root.classList.add('wh-design');
  window.WH = WH;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    boot();
    if (document.readyState === 'complete') {
      start();
    } else {
      document.addEventListener('DOMContentLoaded', start);
      window.addEventListener('load', start);
    }
  }
})(window, document);
